import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { GlobalStateProvider } from "./contexts/GlobalStateContext";
import ErrorBoundary from "./components/common/ErrorBoundary";
import NetworkStatus from "./components/common/NetworkStatus";
import ProtectedRoute from "./components/common/ProtectedRoute";
import PublicRoute from "./components/common/PublicRoute";
import RequirePasswordChange from "./components/common/RequirePasswordChange";
import ModeratorRoute from "./components/common/ModeratorRoute";
import AdminOnlyRoute from "./components/common/AdminOnlyRoute";
import Home from "./pages/Home";
import Register from "./pages/Register";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ModeratorDashboard from "./pages/ModeratorDashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Orders from "./pages/Orders";
import ReferralReports from "./pages/ReferralReports";
import ItemManagement from "./pages/ItemManagement";
import LocationManagement from "./pages/LocationManagement";
import ChangePassword from "./pages/ChangePassword";
import { ROUTES } from "./utils/constants";
import "./App.css";

function App() {
  return (
    <ErrorBoundary>
      <GlobalStateProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Home landing page */}
                <Route
                  path={ROUTES.HOME}
                  element={
                    <ErrorBoundary>
                      <Home />
                    </ErrorBoundary>
                  }
                />

                {/* Public registration page - always accessible */}
                <Route
                  path={ROUTES.REGISTER}
                  element={
                    <ErrorBoundary>
                      <PublicRoute redirectIfAuthenticated={false}>
                        <Register />
                      </PublicRoute>
                    </ErrorBoundary>
                  }
                />

                {/* Admin login - only accessible when not authenticated */}
                <Route
                  path={ROUTES.ADMIN_LOGIN}
                  element={
                    <ErrorBoundary>
                      <PublicRoute redirectIfAuthenticated={true}>
                        <AdminLogin />
                      </PublicRoute>
                    </ErrorBoundary>
                  }
                />

                {/* Change password - protected route, no password change requirement */}
                <Route
                  path={ROUTES.CHANGE_PASSWORD}
                  element={
                    <ErrorBoundary>
                      <ProtectedRoute>
                        <ChangePassword />
                      </ProtectedRoute>
                    </ErrorBoundary>
                  }
                />

                {/* Forgot password - public */}
                <Route
                  path={ROUTES.FORGOT_PASSWORD}
                  element={
                    <ErrorBoundary>
                      <ForgotPassword />
                    </ErrorBoundary>
                  }
                />

                {/* Orders - moderator or admin */}
                <Route
                  path={ROUTES.ORDERS}
                  element={
                    <ErrorBoundary>
                      <ProtectedRoute>
                        <RequirePasswordChange>
                          <ModeratorRoute>
                            <Orders />
                          </ModeratorRoute>
                        </RequirePasswordChange>
                      </ProtectedRoute>
                    </ErrorBoundary>
                  }
                />

                {/* Reset password - public, token-based */}
                <Route
                  path={ROUTES.RESET_PASSWORD}
                  element={
                    <ErrorBoundary>
                      <ResetPassword />
                    </ErrorBoundary>
                  }
                />

                {/* Admin dashboard - admin only (moderators redirected to Moderator Dashboard) */}
                <Route
                  path={ROUTES.ADMIN_DASHBOARD}
                  element={
                    <ErrorBoundary>
                      <ProtectedRoute>
                        <RequirePasswordChange>
                          <AdminOnlyRoute>
                            <AdminDashboard />
                          </AdminOnlyRoute>
                        </RequirePasswordChange>
                      </ProtectedRoute>
                    </ErrorBoundary>
                  }
                />

                {/* Moderator dashboard - moderator or admin */}
                <Route
                  path={ROUTES.MODERATOR_DASHBOARD}
                  element={
                    <ErrorBoundary>
                      <ProtectedRoute>
                        <RequirePasswordChange>
                          <ModeratorRoute>
                            <ModeratorDashboard />
                          </ModeratorRoute>
                        </RequirePasswordChange>
                      </ProtectedRoute>
                    </ErrorBoundary>
                  }
                />

                {/* Referral reports - admin only */}
                <Route
                  path={ROUTES.REFERRAL_REPORTS}
                  element={
                    <ErrorBoundary>
                      <ProtectedRoute>
                        <RequirePasswordChange>
                          <AdminOnlyRoute>
                            <ReferralReports />
                          </AdminOnlyRoute>
                        </RequirePasswordChange>
                      </ProtectedRoute>
                    </ErrorBoundary>
                  }
                />

                {/* Item management - moderator or admin only */}
                <Route
                  path={ROUTES.ITEM_MANAGEMENT}
                  element={
                    <ErrorBoundary>
                      <ProtectedRoute>
                        <RequirePasswordChange>
                          <ModeratorRoute>
                            <ItemManagement />
                          </ModeratorRoute>
                        </RequirePasswordChange>
                      </ProtectedRoute>
                    </ErrorBoundary>
                  }
                />

                {/* Location management - moderator or admin only */}
                <Route
                  path={ROUTES.LOCATION_MANAGEMENT}
                  element={
                    <ErrorBoundary>
                      <ProtectedRoute>
                        <RequirePasswordChange>
                          <ModeratorRoute>
                            <LocationManagement />
                          </ModeratorRoute>
                        </RequirePasswordChange>
                      </ProtectedRoute>
                    </ErrorBoundary>
                  }
                />

                {/* Catch-all route redirects to home */}
                <Route
                  path="*"
                  element={<Navigate to={ROUTES.HOME} replace />}
                />
              </Routes>

              {/* Global Network Status Indicator */}
              <NetworkStatus position="bottom-left" />
            </div>
          </Router>
        </AuthProvider>
      </GlobalStateProvider>
    </ErrorBoundary>
  );
}

export default App;
